import type { MutatingScreenKind, PropertyDescription } from "@glide/app-description";
import { ArrayScreenFormat, getColumnProperty, makeColumnProperty } from "@glide/app-description";
import { type InputOutputTables } from "@glide/common-core/dist/js/description";
import { getTableName } from "@glide/type-schema";
import { GroupingSupport } from "@glide/component-utils";
import type { WireAppCheckListComponent, WireAppCheckListItem } from "@glide/fluent-components/dist/js/base-components";
import {
    type AppDescriptionContext,
    type PropertyDescriptor,
    type PropertyTableGetter,
    ColumnPropertyFlag,
    ColumnPropertyHandler,
    PropertySection,
    SwitchPropertyHandler,
    getPrimitiveColumnsSpec,
} from "@glide/function-utils";
import {
    type WireTableComponentHydratorConstructor,
    type WireAction,
    WireComponentKind,
    type WireInflationBackend,
} from "@glide/wire";
import { defined } from "@glideapps/ts-necessities";
import { getActionsForArrayContent } from "../components/component-utils";
import {
    type WireStringGetter,
    getAppArrayScreenEmptyMessage,
    inflateActionsWithCanAutoRun,
    inflateBooleanProperty,
    makeSimpleWireTableComponentHydratorConstructor,
    spreadComponentID,
} from "../wire/utils";
import {
    type SummaryArrayContentDescription,
    type SummaryArrayScreenDescription,
    SummaryArrayScreenHandler,
    getNumberToTruncateTo,
    hydrateListItem,
    inflateSummary,
    makeAppListGroups,
} from "./summary-array-screen";

function makeCheckedFlagNamePropertyHandler(getIndirectTable: PropertyTableGetter | undefined): ColumnPropertyHandler {
    return new ColumnPropertyHandler(
        "checkedFlagName",
        "Check Value",
        [
            ColumnPropertyFlag.Required,
            ColumnPropertyFlag.Editable,
            ColumnPropertyFlag.DefaultCaption,
            ColumnPropertyFlag.EditedInApp,
        ],
        getIndirectTable,
        undefined,
        getPrimitiveColumnsSpec,
        "boolean",
        PropertySection.Content
    );
}

const allowWrappingPropertyHandler = new SwitchPropertyHandler(
    { allowWrapping: false },
    "Allow text wrapping",
    PropertySection.TextStyle
);

interface CheckListArrayContentDescription extends SummaryArrayContentDescription {
    // This is the legacy of a bug.  The `checkedFlagName` was previously
    // named `propertyName` which collides with the same property in
    // inline list.
    readonly propertyName?: PropertyDescription;
    readonly checkedFlagName: PropertyDescription;
    readonly allowWrapping?: PropertyDescription;
}

function getCheckedFlagName(desc: CheckListArrayContentDescription, insideInlineList: boolean): string | undefined {
    // FIXME: It's a hack to make this property handler without a `getIndirectTable`
    // here.  We're only using it to get the value in the description, for which
    // case the indirect table doesn't matter, but this is not a nice way to do it.
    let propertyName = makeCheckedFlagNamePropertyHandler(undefined).getColumnName(desc);
    // See comment on `propertyName` above.
    if (propertyName === undefined && !insideInlineList) {
        propertyName = getColumnProperty(desc.propertyName);
    }
    return propertyName;
}

interface CheckListArrayScreenDescription extends SummaryArrayScreenDescription, CheckListArrayContentDescription {}

export class CheckListArrayScreenHandler extends SummaryArrayScreenHandler<
    CheckListArrayContentDescription,
    CheckListArrayScreenDescription
> {
    protected readonly supportsNonURLImages = false;
    protected readonly supportsEmojiImages = true;
    protected readonly supportsTruncateList = true;

    constructor() {
        super(ArrayScreenFormat.CheckList, "Checklist", "checklist", false);
    }

    protected get groupingSupport(): GroupingSupport {
        return GroupingSupport.Regular;
    }

    protected getInterSummaryPropertyDescriptors(
        getPropertyTable: PropertyTableGetter | undefined,
        _insideInlineList: boolean
    ): readonly PropertyDescriptor[] {
        return [makeCheckedFlagNamePropertyHandler(getPropertyTable)];
    }

    public getContentPropertyDescriptors<T extends CheckListArrayContentDescription>(
        getPropertyTable: PropertyTableGetter | undefined,
        insideInlineList: boolean,
        containingScreenTables: InputOutputTables | undefined,
        desc: T | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean,
        forEasyTabConfiguration: boolean,
        isFirstComponent: boolean | undefined
    ): ReadonlyArray<PropertyDescriptor> {
        const descrs: PropertyDescriptor[] = [
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                containingScreenTables,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms,
                forEasyTabConfiguration,
                isFirstComponent
            ),
        ];

        descrs.push(allowWrappingPropertyHandler);
        return descrs;
    }

    public inflateContent<T extends CheckListArrayContentDescription>(
        ib: WireInflationBackend,
        desc: T,
        captionGetter: WireStringGetter | undefined,
        containingRowIB: WireInflationBackend | undefined,
        componentID: string | undefined
    ): WireTableComponentHydratorConstructor | undefined {
        const { forBuilder } = ib;
        const tableName = getTableName(ib.tables.input);
        const columnName = getCheckedFlagName(desc, containingRowIB !== undefined);
        if (columnName === undefined) return undefined;

        const [checkedGetter, checkedGetterType] = inflateBooleanProperty(ib, makeColumnProperty(columnName));
        if (checkedGetterType === undefined) return undefined;

        const allowWrapping = allowWrappingPropertyHandler.getDefinedSwitch(desc);

        const summaryGetters = inflateSummary(ib, desc);
        const groupByGetters = this.makeGroupByGetters(ib, desc);
        const { actionHydrator, canAutoRunAction } = inflateActionsWithCanAutoRun(
            ib,
            getActionsForArrayContent(this, ib.tables, desc, ib.adc).actions
        );
        const numToTruncate = getNumberToTruncateTo(desc, containingRowIB !== undefined, 1, this.groupingSupport);

        return makeSimpleWireTableComponentHydratorConstructor(ib, (thb, chb, searchActive) => {
            let firstListItemActionToRun: WireAction | undefined;
            const groups = makeAppListGroups<WireAppCheckListItem>(
                thb,
                groupByGetters,
                numToTruncate,
                tableName,
                rhb => {
                    const token = rhb.registerOnValueChange("edit", columnName);
                    if (token === false) return undefined;
                    const item = {
                        ...hydrateListItem(rhb, summaryGetters, actionHydrator),
                        // It's important that the key is the row ID because
                        // we use it for the ##selectedMasterItemKey.
                        key: defined(rhb.rowContext?.inputRows[0].$rowID),
                        checked: { value: checkedGetter(rhb) ?? false, onChangeToken: token },
                        image: summaryGetters.imageGetter(rhb),
                    };
                    if (canAutoRunAction && firstListItemActionToRun === undefined) {
                        firstListItemActionToRun = item.action;
                    }
                    return item;
                }
            );
            if (groups === undefined) return undefined;

            const component: WireAppCheckListComponent = {
                ...spreadComponentID(componentID, forBuilder),
                kind: WireComponentKind.List,
                format: ArrayScreenFormat.CheckList,
                title: captionGetter?.(defined(chb)) ?? "",
                emptyMessage: getAppArrayScreenEmptyMessage(searchActive),

                allowWrapping,
                groups,
            };
            return {
                component,
                isValid: true,
                firstListItemActionToRun,
            };
        });
    }
}
